(function () {
    function PermissionService(
        $rootScope,
        APIService,
        $q,
        $state,
        ErrorService,
        DashboardConfig,
        AnalyticsService
    ) {

        const self = this;

        self.user = undefined;

        self.rolesLookup = [];

        self.permissions = [];

        self.contextRoles = [];

        const initialisedDeferred = $q.defer();

        /**
         * Returns a promise for when the service is first fully initialised
         * @type {Promise}
         */
        self.$initialised = initialisedDeferred.promise;

        /**
         * We use the current state of things on the Permission service
         * to set up the user context. For example, if self.brandId is set
         * we set up a brand context for the user
         * @returns {Promise}
         */
        self.setUserContextForCurrentState = function setUserContextForCurrentState() {
            const contextDeferred = $q.defer();

            // Don't get the current context from the database if the context is already set
            if (self.userContext) {
                const brandContextUnChanged = self.brandId === self.userContext.BrandId;
                const callCenterContextUnChanged = self.callCenterId === self.userContext.CallCenterId;
                const storeContextUnChanged = self.storeId === self.userContext.StoreId;
                if (brandContextUnChanged || callCenterContextUnChanged || storeContextUnChanged) {
                    contextDeferred.resolve();
                    return contextDeferred.promise;
                }
            }

            // If any context variables are found in the state params then set the user's context
            if (self.brandId) {
                self.userContext = new Dashboard.Models.Permissions.RoleContext();
                self.userContext.ContextType = Dashboard.ContextTypes.BrandContext;
                self.userContext.BrandId = self.brandId;
                APIService.Brands
                    .getBrandsByBrandIds([ self.brandId ])
                    .then(result => {
                            if (result.data.length > 0) {
                                self.userContext.BrandName = result.data[0].Name;
                                contextDeferred.resolve();
                            } else {
                                contextDeferred.reject();
                            }
                        }
                    ).catch(e => contextDeferred.reject);
            } else if (self.callCenterId) {
                self.userContext = new Dashboard.Models.Permissions.RoleContext();
                self.userContext.ContextType = Dashboard.ContextTypes.CallCenterContext;
                self.userContext.CallCenterId = self.callCenterId;
                self.userContext.CallCenterGroups = (self.user.Roles || [])
                    .filter(o => o.RoleId === Dashboard.Models.RoleLookup.CallCenterManager)
                    .flatMap(o => o.GroupId)
                    .map(o => ({ CallCenterId: self.callCenterId, GroupId: o }));
                APIService.CallCenters.getCallCentersByCallCenterIds([ self.callCenterId ]).then(
                    function (result) {
                        if (result.data.length > 0) {
                            self.userContext.CallCenterName = result.data[0].Name;
                            contextDeferred.resolve();
                        } else {
                            contextDeferred.reject();
                        }
                    }
                ).catch(contextDeferred.reject);
            } else if (self.storeId) {
                self.userContext = new Dashboard.Models.Permissions.RoleContext();
                self.userContext.ContextType = Dashboard.ContextTypes.StoreContext;
                self.userContext.StoreId = self.storeId;
                APIService.Stores.getStoresByStoreIds([ self.storeId ]).then(
                    function (result) {
                        if (result.data.length > 0) {
                            self.userContext.StoreName = result.data[0].Name;
                            contextDeferred.resolve();
                        } else {
                            contextDeferred.reject();
                        }
                    }
                ).catch(contextDeferred.reject);
            } else {
                contextDeferred.resolve();
            }

            return contextDeferred.promise;
        };

        function updateLiveChat() {
            if (DashboardConfig.Mode === "development") {
                return;
            }
            const userInformation = `${ DashboardConfig.APIServer }${ Dashboard.LiveChat.userInformation }${ self.user.Id }`;
            const storeInformation = self.userContext.StoreId
                ? `${ DashboardConfig.APIServer }${ Dashboard.LiveChat.storeInformation }${ self.userContext.StoreId }`
                : ""
            if (!window.LiveChatWidget) {
                return;
            }
            LiveChatWidget.call("set_customer_name", self.user.FullName);
            LiveChatWidget.call("set_customer_email", self.user.Email);
            LiveChatWidget.call("update_session_variables", {
                user_id: self.user.Id,
                user_name: self.user.FullName,
                user_mobile: self.user.MobileNumber,
                store_id: self.userContext.StoreId || "",
                store_name: self.userContext.StoreName || "",
                userInformation: userInformation,
                storeInformation: storeInformation
            });
        }

        /**
         * Initialise the user's permissions and context
         * @param $stateParams
         */
        self.verifyUserPermissionsForState = function ($stateParams) {
            const deferred = $q.defer();

            // Get the param value from the param string that could look like "brand-123"
            const getParamValue = function (paramValue) {
                const numberPattern = /\d+/g;
                let result = undefined;
                if (paramValue) {
                    const match = numberPattern.exec(paramValue);
                    result = Number(match[0]);
                }
                return result;
            };

            // Get the context from the state params
            self.brandId = getParamValue($stateParams.brandId);
            self.callCenterId = getParamValue($stateParams.callCenterId);
            self.storeId = getParamValue($stateParams.storeId);

            // Populate the user
            const getUser = function () {
                return self.getUser().then(
                    function success(result) {
                        self.user = new Dashboard.Models.User();
                        self.user = self.user.initialiseUser(result.data);
                        return result;
                    }
                );
            };

            // Populate the permissions
            const getPermissions = function () {
                return self.getAllPermissionsRoles().then(
                    function (result) {
                        self.permissions = result;
                        return result;
                    }
                );
            };

            // Populate the context roles lookup
            const getUserContextRoles = function () {
                return self.getUserContextRolesLookup().then(
                    function (result) {
                        self.contextRoles = result.data;
                        return result;
                    }
                );
            };

            // If the user has not been populated, then populate it and check if they have access
            if (!self.user) {
                $q.all([ getUser(), getPermissions(), getUserContextRoles() ]).then(
                    self.setUserContextForCurrentState.bind(self)
                ).then(
                    function () {
                        self.user.initialiseUserContext(self.contextRoles);
                        const hasDashboardAccess = self.getRolesForPermission(Dashboard.Models.Lookups.Permissions.DashboardAccess)
                        const result = self.doesUserHaveAnyPermission([ Dashboard.Models.Lookups.Permissions.DashboardAccess ]);
                        if (!hasDashboardAccess) {
                            deferred.resolve(false);
                            self.navigateToAccessDenied();
                        }

                        if (!self.getUserContext()) {
                            deferred.resolve(false);
                            $rootScope.$broadcast(Dashboard.Events.UserContextNotSet, true);
                        } else {
                            deferred.resolve(result);
                        }
                    }
                ).catch(
                    function (result) {
                        ErrorService.showError("An unexpected error has occurred", result);
                        deferred.resolve(false);
                    }
                );
            } else {
                self.setUserContextForCurrentState().then(function () {
                    deferred.resolve(true);
                }).catch(function () {
                    deferred.reject();
                })
            }

            // Return a promise and ensure that we mark the service as initialised (if it isn't already)
            return deferred.promise.then(function () {
                initialisedDeferred.resolve();
            });
        };

        self.notifyContextChanged = function () {
            $rootScope.$broadcast(Dashboard.Events.UserContextChanged, true);
            if (!self.user && !self.userContext) {
                return;
            }
            const brandRoles = getRolesForContext(self.contextRoles.Brand);
            const callCenterRoles = getRolesForContext(self.contextRoles.CallCenter);
            const storeRoles = getRolesForContext(self.contextRoles.Store);
            AnalyticsService.identify(self.user.Id, {
                name: self.user.FullName,
                email: self.user.Email,
                roles: {
                    brandRoles,
                    callCenterRoles,
                    storeRoles
                }
            });
            updateLiveChat();
        };

        function getRolesForContext(contextRoles) {
            return contextRoles.map(brandRole => {
                return self.user.Roles.find(role => role.RoleId === brandRole)
            }).filter(Boolean);
        }

        self.getUserBrands = function () {
            return self.getUserContextBrands().catch(function (results) {
                ErrorService.showError("An unexpected error has occurred", results);
            })
        };

        self.getUserCallCenters = function () {
            return self.getUserContextCallCenters().catch(function (results) {
                ErrorService.showError("An unexpected error has occured", results);
            })
        };

        self.getUserStores = function () {
            return self.getUserContextStores().catch(function (results) {
                ErrorService.showError("An unexpected error has occured", results);
            })
        };

        self.getUser = function () {
            return APIService.Account.getUserProfile();
        };

        self.getAllPermissionsRoles = function () {
            function extractRolesFromPermissions(permissionsArray) {
                const roles = permissionsArray.reduce((acc, cur) => {
                    return acc.concat(cur.Roles);
                }, []);
                return [ ...new Map(roles.map(item => [ item['Id'], item ])).values() ];
            }

            return APIService.Permissions.getAllPermissionsRoles().then((result) => {
                    self.rolesLookup = extractRolesFromPermissions(result.data || []);
                    return (result.data || []).reduce((acc, cur) => {
                        acc[cur.PermissionName] = cur
                        return acc;
                    }, {});
                }
            );
        };

        self.getRoleNameById = function (id) {
            const role = self.rolesLookup.find(item => item.Id === id);
            if (!role) {
                throw new Error(`Role #${ id } is unknown within the context of roles manageable at dashboard`);
            }
            return role.Name;
        }

        /**
         * Returns the roles for contexts lookup
         * @returns {*}
         */
        self.getUserContextRolesLookup = function () {
            return APIService.Permissions.getRolesForContextLookup();
        };

        /**
         *
         * @param userContext
         * @param userRoles {*[]}
         * @returns {boolean}
         */
        self.userHasAccess = function (userContext, userRoles) {
            if (!userContext) {
                return false;
            }
            // If the user is a system administrator we do not need to validate their permissions
            if (!!userRoles.find((userRole) => userRole.RoleId === Dashboard.Models.RoleLookup.SystemAdministrator)) {
                return true;
            }
            return !!userRoles.find((userRole) => {
                const userRoleIsForStoreContext = !!userRole.StoreId;
                if (userRoleIsForStoreContext) {
                    return (userRole.StoreId && userContext.StoreId === userRole.StoreId)
                }
                let hasAccess = true;
                if (userContext.isStoreContext() && userRole.StoreIds && userRole.StoreIds.length > 0) {
                    hasAccess = userRole.StoreIds.find(id => id === userContext.StoreId);
                }
                const userRoleIsForCallCenterContext = !!userRole.CallCenterId
                if (userRoleIsForCallCenterContext) {
                    return hasAccess && userContext.CallCenterId === userRole.CallCenterId;
                }
                const userRoleIsForBrandContext = !!userRole.BrandId;
                if (userRoleIsForBrandContext) {
                    return userContext.BrandId === userRole.BrandId;
                }
                const userRoleIsForProvinceContext = !!userRole.ProvinceId
                if (userRoleIsForProvinceContext) {
                    return userContext.ProvinceId === userRole.ProvinceId;
                }
                const userRoleIsForGroupContext = !!userRole.GroupId
                    || (userRole.GroupIds && userRole.GroupIds.length > 0)
                if (userRoleIsForGroupContext) {
                    return userContext.GroupId === userRole.GroupId
                        || userRole.GroupIds.find((groupId) => groupId === userContext.GroupId);
                }
                return hasAccess;
            });
        }

        /**
         * @param permission {string}
         * @returns {[]}
         */
        self.getRolesForPermission = function (permission) {
            const appPermission = self.permissions[permission];
            if (!appPermission) {
                return [];
            }
            return self.user.getRolesForPermission(appPermission);
        }

        /**
         * Checks if a user has access to a given permission and context
         * @param permission {string}
         * @param context {Dashboard.Models.Permissions.RoleContext}
         * @returns {boolean}
         * @deprecated
         */
        self.doesUserHavePermission = function (permission, context = null) {
            /**
             * To ensure correct use of these functions, this is to guarantee that all places
             * that have this specific check used, it is used correctly. When context has to be
             * provided, that has to be specific, hence the error. When no context is not needed
             * i.e. Access to reports link, then context as a check has to be provided, provided
             * to the correct function for predictable results.
             * The error is more specific than just removing the argument completely, as it helps point
             * to the fix.
             */
            if (context) {
                throw Error('use `doesUserHavePermissionForContext` instead');
            }

            const userRoles = self.getRolesForPermission(permission)
            return userRoles.length > 0;
        };

        self.doesUserHavePermissionForContext = function (permission, userContext) {
            const context = userContext || self.userContext;
            const userRoles = self.getRolesForPermission(permission);
            return self.userHasAccess(context, userRoles);
        }

        self.doesUserHavePermissionToEditUsers = function () {
            return self.doesUserHavePermissionForContext(
                Dashboard.Models.Lookups.Permissions.ManageUsers
            ) || self.doesUserHavePermissionForContext(
                Dashboard.Models.Lookups.Permissions.ManageNonAdminUsers
            );
        }

        /** @param {string[]} permissions */
        self.doesUserHaveAnyPermission = function (permissions) {
            if (!Array.isArray(permissions) || permissions.length === 0) {
                return false;
            }

            return permissions.some((permission) =>
                self.doesUserHavePermissionForContext(permission, self.userContext))
        }

        self.doesUserHaveRole = function (role) {
            return self.user.userHasRole(role);
        }

        /**
         * Navigates the user to the access denied page
         */
        self.navigateToAccessDenied = function () {
            $state.go("accessdenied");
        };

        /**
         * Gets the user's current context
         * @returns {Dashboard.Models.Permissions.RoleContext}
         */
        self.getUserContext = function () {
            return self.userContext;
        };

        self.getUserContextBrands = function () {
            const deferred = $q.defer();

            // Return the brand ids from the brand roles
            const brandIds = self.getUserContextBrandIds();

            if (brandIds.length === 0) {
                deferred.resolve([]);
                return deferred.promise;
            }

            APIService.Brands.getBrandsByBrandIds(brandIds).then(
                function (results) {
                    deferred.resolve(results.data);
                },
                function fail() {
                    deferred.reject();
                }
            );

            return deferred.promise;
        };

        /**
         * Return the user's brand ids based on their roles
         * @returns {Array}
         */
        self.getUserContextBrandIds = function () {
            if (!self.user) {
                return [];
            }
            // Get all the roles for a brand context
            const brandRoles = self.user.Roles.filter((role) => {
                return self.contextRoles.Brand.indexOf(role.RoleId) > -1;
            });

            if (brandRoles.length === 0) {
                return [];
            }

            // Return the brand ids from the brand roles
            let brandIds = brandRoles.map((brandRole) => {
                return brandRole.BrandId;
            }).filter(Boolean);

            // Make sure there are no duplicates as this array is built up from multiple roles
            brandIds = [ ...new Set(brandIds) ];

            return brandIds;
        };

        /**
         * Returns a brand id from a role that has a specific store id
         * @param storeId {number}
         * @returns {number}
         */
        self.getBrandIdForStoreRole = function (storeId) {
            if (!self.user) {
                return 0;
            }
            // Get all the roles for a store context
            const storeRoles = self.user.Roles.filter((role) => {
                return self.contextRoles.Store.indexOf(role.RoleId) > -1;
            });

            const storeRole = storeRoles.find((role) => {
                return role.StoreIds.includes(storeId);
            });

            if (!storeRole) {
                return 0;
            }
            return storeRole.BrandId;
        };

        /**
         * Gets the user's callcenters available based on their role
         * @returns {promise}
         */
        self.getUserContextCallCenters = function () {
            const deferred = $q.defer();

            // Return the call center ids from the call center roles
            const callCenterIds = self.getUserContextCallCenterIds();

            if (callCenterIds.length === 0) {
                deferred.resolve([]);
                return deferred.promise;
            }

            APIService.CallCenters.getCallCentersByCallCenterIds(callCenterIds).then(
                function (results) {
                    deferred.resolve(results.data);
                },
                function fail() {
                    deferred.reject();
                }
            );

            return deferred.promise;
        };

        /**
         * Returns the user's call center ids based on their roles
         * @returns {Array}
         */
        self.getUserContextCallCenterIds = function () {
            if (!self.user) {
                return [];
            }
            // Get all the roles for a callcenter context
            const callCenterRoles = self.user.Roles.filter((role) => {
                return self.contextRoles.CallCenter.indexOf(role.RoleId) > -1;
            });

            if (callCenterRoles.length === 0) {
                return [];
            }

            // Return the call center ids from the call center roles
            let callCenterIds = callCenterRoles.map((callCenterRole) => {
                return callCenterRole.CallCenterId;
            }).filter(id => !!id && id > 0);

            // Make sure there are no duplicates as this array is built up from multiple roles
            callCenterIds = [ ...new Set(callCenterIds) ];

            return callCenterIds;
        };

        /**
         * Gets the user's stores available based on their role
         * @returns {promise}
         */
        self.getUserContextStores = function () {
            const deferred = $q.defer();

            const storeIds = self.getUserContextStoreIds();

            if (storeIds.length === 0) {
                deferred.resolve([]);
                return deferred.promise;
            }

            APIService.Stores.getStoresByStoreIds(storeIds).then(
                function (results) {
                    deferred.resolve(results.data);
                },
                function fail() {
                    deferred.reject();
                }
            );

            return deferred.promise;
        };

        /**
         * Return the user's store ids based on their roles
         * @returns {Array}
         */
        self.getUserContextStoreIds = function () {
            if (!self.user) {
                return [];
            }
            // Get all the roles for a store context
            const storeRoles = self.user.Roles.filter((role) => {
                return self.contextRoles.Store.indexOf(role.RoleId) > -1;
            });

            if (storeRoles.length === 0) {
                return [];
            }

            let storeIds = [];
            storeRoles.forEach((storeRole) => {
                storeIds = storeIds.concat(storeRole.StoreIds)
            });

            // Make sure there are no duplicates as this array is built up from multiple roles
            storeIds = [ ...new Set(storeIds) ];

            return storeIds;
        };
    }

    angular.module("dashboard")
        .service("PermissionService", [
            "$rootScope",
            "APIService",
            "$q",
            "$state",
            "ErrorService",
            "DashboardConfig",
            "AnalyticsService",
            PermissionService
        ]);

})();
